/**
 * WP-50 — shifting formula references on an insertion (plan 8.1 "column and
 * row insertion", plan 8.3 which inserts the WCR rate / Difference /
 * Justification columns into the sponsor's own structure).
 *
 * Excel's rule, and the one implemented here: inserting `count` columns at
 * column `at` moves every reference whose column is >= `at` by `count`,
 * whether or not it is anchored with `$`. A range whose start is before the
 * insertion and whose end is at or after it therefore WIDENS to cover the new
 * columns — which is exactly what a section SUM must do when the WCR columns
 * land inside it. Rows work the same way.
 *
 * The rewrite is token-driven, never a regex over raw text: tokenizeFormula
 * (the shared engine, plan 6.3) is what tells a real reference apart from the
 * text inside a string literal such as ="see C12 note". A formula the
 * tokenizer cannot read is returned untouched and reported (G3, G13) — the
 * caller puts it in the insertion event's `unshifted` list.
 */
import { tokenizeFormula, FormulaToken } from './engineBridge';

export interface ShiftSpec {
  /** The sheet whose columns or rows moved. */
  targetSheet: string;
  axis: 'col' | 'row';
  /** 1-based index the insertion happened at. */
  at: number;
  count: number;
}

export interface ShiftResult {
  /** 'unshiftable' means the tokenizer could not read it: nothing was touched. */
  status: 'changed' | 'unchanged' | 'unshiftable';
  formula: string;
  /**
   * Non-null whenever the caller must report this formula: either it could not
   * be read at all, or it holds a reference this grid does not own the
   * geometry of (a 3D sheet span).
   */
  reason: string | null;
}

const RE_CELL_BODY = /^(\$?)([A-Za-z]{1,3})(\$?)([0-9]{1,7})$/;
const RE_COL_BODY = /^(\$?)([A-Za-z]{1,3})$/;
const RE_ROW_BODY = /^(\$?)([0-9]{1,7})$/;

function sameSheet(a: string, b: string): boolean {
  return String(a).trim().toUpperCase() === String(b).trim().toUpperCase();
}

function letterToIndex(letters: string): number | null {
  let n = 0;
  for (let i = 0; i < letters.length; i += 1) {
    const c = letters.charCodeAt(i);
    let v;
    if (c >= 65 && c <= 90) v = c - 64;
    else if (c >= 97 && c <= 122) v = c - 96;
    else return null;
    n = n * 26 + v;
  }
  return n;
}

function indexToLetter(index: number): string {
  let n = index;
  let out = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    out = String.fromCharCode(65 + rem) + out;
    n = Math.floor((n - 1) / 26);
  }
  return out;
}

function shiftIndex(index: number, spec: ShiftSpec): number {
  return index >= spec.at ? index + spec.count : index;
}

/** Rewrite one half of a reference body ('C7', '$C$7', 'C', '7'). */
function shiftBodyPart(part: string, spec: ShiftSpec): string {
  const cell = RE_CELL_BODY.exec(part);
  if (cell) {
    const col = letterToIndex(cell[2]);
    const row = parseInt(cell[4], 10);
    if (col === null) return part;
    const nextCol = spec.axis === 'col' ? shiftIndex(col, spec) : col;
    const nextRow = spec.axis === 'row' ? shiftIndex(row, spec) : row;
    return cell[1] + indexToLetter(nextCol) + cell[3] + String(nextRow);
  }
  const colOnly = RE_COL_BODY.exec(part);
  if (colOnly) {
    if (spec.axis !== 'col') return part;
    const col = letterToIndex(colOnly[2]);
    if (col === null) return part;
    return colOnly[1] + indexToLetter(shiftIndex(col, spec));
  }
  const rowOnly = RE_ROW_BODY.exec(part);
  if (rowOnly) {
    if (spec.axis !== 'row') return part;
    return rowOnly[1] + String(shiftIndex(parseInt(rowOnly[2], 10), spec));
  }
  return part;
}

function splitSheetPrefix(text: string, hasSheet: boolean): { prefix: string; body: string } {
  if (!hasSheet) return { prefix: '', body: text };
  const i = text.lastIndexOf('!');
  if (i < 0) return { prefix: '', body: text };
  return { prefix: text.slice(0, i + 1), body: text.slice(i + 1) };
}

function rewriteToken(token: FormulaToken, formulaSheet: string, spec: ShiftSpec): string | null {
  if (token.type !== 'ref' && token.type !== 'range') return null;
  const text = String(token.value);
  const hasSheet = token.sheet !== null && token.sheet !== undefined;
  const owner = hasSheet ? String(token.sheet) : formulaSheet;
  if (!sameSheet(owner, spec.targetSheet)) return null;
  const split = splitSheetPrefix(text, hasSheet);
  const parts = split.body.split(':');
  const shifted = parts.map((p) => shiftBodyPart(p, spec)).join(':');
  const next = split.prefix + shifted;
  return next === text ? null : next;
}

/**
 * Shift every reference in `formula` that points at the inserted-into sheet.
 * `formulaSheet` is the sheet the formula itself lives on — that is what an
 * unprefixed reference means.
 */
export function shiftFormula(formula: string, formulaSheet: string, spec: ShiftSpec): ShiftResult {
  if (typeof formula !== 'string' || formula.trim().length === 0) {
    return { status: 'unchanged', formula: formula, reason: null };
  }
  if (spec.count === 0) return { status: 'unchanged', formula: formula, reason: null };

  const lead = /^\s*=\+?|^\s*/.exec(formula);
  const prefix = lead ? lead[0] : '';
  const body = formula.slice(prefix.length);

  let tokens: FormulaToken[];
  try {
    tokens = tokenizeFormula(body);
  } catch (e) {
    const reason = e instanceof Error ? e.message : 'formula could not be tokenized';
    return { status: 'unshiftable', formula: formula, reason: reason };
  }

  let out = '';
  let cursor = 0;
  let changed = false;
  let spans3d = false;
  // End offset of the last sheet3d / external prefix token. The tokenizer emits
  // the QUOTED form of both as a PREFIX token only ('First:Last'! and
  // '[Book.xlsx]Sheet'!), and scans the cell that follows on its own: a bare
  // 'ref' carrying sheet null, which would otherwise be read as a reference on
  // this sheet and shifted. Only the unquoted [1]Sheet1!C2 form arrives whole.
  let prefixEnd = -1;
  for (let i = 0; i < tokens.length; i += 1) {
    const token = tokens[i];
    if ((token.type === 'ref' || token.type === 'range') && token.start === prefixEnd) {
      // The cell half of the prefix above. Its geometry is not this grid's, so
      // it is left exactly as found (G3, G13).
      prefixEnd = -1;
      continue;
    }
    if (token.type === 'sheet3d') {
      // A 3D sheet span ('First:Last'!C2). Whether the inserted-into sheet is
      // inside the span is not something the grid can tell, so nothing in the
      // formula is guessed at — it is reported instead (G3).
      spans3d = true;
      prefixEnd = token.end;
      continue;
    }
    if (token.type === 'external') {
      // A link into another workbook. Inserting a column HERE does not move a
      // cell THERE, so leaving it alone is the correct answer, not a gap.
      prefixEnd = token.end;
      continue;
    }
    const replacement = rewriteToken(token, formulaSheet, spec);
    if (replacement === null) continue;
    out += body.slice(cursor, token.start) + replacement;
    cursor = token.end;
    changed = true;
  }

  const warning = spans3d
    ? 'formula spans a 3D sheet range; its references were left as found'
    : null;
  // A 3D span is reported, never partly rewritten: the message above, this
  // branch's comment and README.md's `unshifted` section all promise the
  // formula came back as found, so it is returned byte-identical (G3, G13).
  if (spans3d) return { status: 'unchanged', formula: formula, reason: warning };
  if (!changed) return { status: 'unchanged', formula: formula, reason: warning };
  out += body.slice(cursor);
  return { status: 'changed', formula: prefix + out, reason: warning };
}
